import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import parsearAvisoPdf from '../hooks/useParsearAvisoPdf';
import { useConfirm } from '../hooks/useConfirm';
import { useTiposIva } from '../hooks/useTiposIva';
import { siniestrosService }   from '../services/siniestrosService';
import { perjudicadosService } from '../services/perjudicadosService';
import { partesService }       from '../services/partesService';
import { importesService }     from '../services/importesService';
import PerjudicadosPanel  from '../components/PerjudicadosPanel';
import ParteTrabajoPanel  from '../components/ParteTrabajoPanel';
import ImportesPanel      from '../components/ImportesPanel';
import PeritoSelector     from '../components/PeritoSelector';
import ConfirmDialog      from '../components/ConfirmDialog';

// ─── Componentes de vista ────────────────────────────────────────────────────

function Campo({ label, valor, href }) {
  if (!valor) return null;
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      {href
        ? <a href={href} className="text-primary-600 font-medium text-sm hover:underline">{valor}</a>
        : <p className="text-sm font-medium text-gray-900">{valor}</p>
      }
    </div>
  );
}


function EstadoToggle({ label, activo, color }) {
  const colores = {
    green:  activo ? 'bg-green-500'  : 'bg-gray-200',
    yellow: activo ? 'bg-yellow-500' : 'bg-gray-200',
    blue:   activo ? 'bg-blue-500'   : 'bg-gray-200',
    red:    activo ? 'bg-red-500'    : 'bg-gray-200',
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${colores[color]}`} />
      <span className={`text-sm ${activo ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>{label}</span>
    </div>
  );
}

// ─── Mini-editores de creación (estado local, sin API) ───────────────────────

function EditorPerjudicados({ lista, onChange }) {
  const VACIO = { perjudicado: '', domicilio: '', telefono1: '', telefono2: '' };
  const [form, setForm] = useState(VACIO);
  const [abierto, setAbierto] = useState(false);

  const handleAdd = () => {
    onChange([...lista, form]);
    setForm(VACIO);
    setAbierto(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Perjudicados
          {lista.length > 0 && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-normal normal-case">
              {lista.length}
            </span>
          )}
        </h3>
        {!abierto && (
          <button onClick={() => setAbierto(true)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {lista.map((p, i) => (
        <div key={i} className="flex items-start justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-900">{p.perjudicado}</p>
            {p.domicilio && <p className="text-xs text-gray-500">{p.domicilio}</p>}
            {(p.telefono1 || p.telefono2) && (
              <p className="text-xs text-gray-400">{[p.telefono1, p.telefono2].filter(Boolean).join(' · ')}</p>
            )}
          </div>
          <button onClick={() => onChange(lista.filter((_, j) => j !== i))}
            className="text-gray-300 hover:text-red-500 ml-2 mt-0.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      ))}

      {abierto && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-0.5 block">Nombre *</label>
              <input value={form.perjudicado} onChange={e => setForm(f => ({ ...f, perjudicado: e.target.value }))}
                placeholder="Nombre del perjudicado"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-0.5 block">Domicilio / Piso</label>
              <input value={form.domicilio} onChange={e => setForm(f => ({ ...f, domicilio: e.target.value }))}
                placeholder="Ej: Piso 2º A"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Teléfono 1</label>
              <input value={form.telefono1} onChange={e => setForm(f => ({ ...f, telefono1: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Teléfono 2</label>
              <input value={form.telefono2} onChange={e => setForm(f => ({ ...f, telefono2: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition">
              Añadir
            </button>
            <button onClick={() => { setAbierto(false); setForm(VACIO); }}
              className="flex-1 border border-gray-300 text-gray-600 text-xs py-1.5 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {lista.length === 0 && !abierto && (
        <p className="text-xs text-gray-400 text-center py-2">Sin perjudicados</p>
      )}
    </div>
  );
}

function EditorPartes({ lista, onChange }) {
  const VACIO = { cTarifa: '', paredes: '', techos: '', superficie: '', piso: '', ubicacion: '' };
  const [form, setForm] = useState(VACIO);
  const [abierto, setAbierto] = useState(false);

  const superficieTotal = lista.reduce((s, p) => s + (Number(p.superficie) || 0), 0);

  const handleAdd = () => {
    const payload = { ...form, superficie: form.superficie ? parseFloat(form.superficie) : null };
    onChange([...lista, payload]);
    setForm(VACIO);
    setAbierto(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Parte de Trabajo
            {lista.length > 0 && (
              <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-normal normal-case">
                {lista.length} zonas
              </span>
            )}
          </h3>
          {superficieTotal > 0 && (
            <p className="text-xs text-blue-600 font-medium mt-0.5">{superficieTotal.toFixed(2)} m² total</p>
          )}
        </div>
        {!abierto && (
          <button onClick={() => setAbierto(true)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {lista.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left py-1.5 font-medium">Tarifa</th>
                <th className="text-left py-1.5 font-medium">Piso</th>
                <th className="text-left py-1.5 font-medium">Ubicación</th>
                <th className="text-center py-1.5 font-medium">Paredes</th>
                <th className="text-center py-1.5 font-medium">Techos</th>
                <th className="text-right py-1.5 font-medium">m²</th>
                <th className="py-1.5 w-6"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {lista.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="py-1.5 text-gray-600">{p.cTarifa || '—'}</td>
                  <td className="py-1.5 text-gray-700">{p.piso || '—'}</td>
                  <td className="py-1.5 font-medium text-gray-900">{p.ubicacion}</td>
                  <td className="py-1.5 text-center text-gray-500">{p.paredes || '—'}</td>
                  <td className="py-1.5 text-center text-gray-500">{p.techos || '—'}</td>
                  <td className="py-1.5 text-right font-medium text-blue-700">
                    {p.superficie != null ? Number(p.superficie).toFixed(2) : '—'}
                  </td>
                  <td className="py-1.5">
                    <button onClick={() => onChange(lista.filter((_, j) => j !== i))}
                      className="text-gray-300 hover:text-red-500">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {abierto && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'cTarifa',    label: 'Tarifa',          placeholder: 'Ej: 3105' },
              { key: 'superficie', label: 'Superficie (m²)', placeholder: '', type: 'number' },
              { key: 'piso',       label: 'Piso',            placeholder: 'Ej: 2º B' },
              { key: 'ubicacion',  label: 'Ubicación',       placeholder: 'Ej: BAÑO, SALON...' },
              { key: 'paredes',    label: 'Paredes',         placeholder: 'X / XXXX' },
              { key: 'techos',     label: 'Techos',          placeholder: 'X / XXXX' },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key}>
                <label className="text-xs text-gray-500 mb-0.5 block">{label}</label>
                <input type={type || 'text'} value={form[key]} step={type === 'number' ? '0.01' : undefined}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition">
              Añadir
            </button>
            <button onClick={() => { setAbierto(false); setForm(VACIO); }}
              className="flex-1 border border-gray-300 text-gray-600 text-xs py-1.5 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {lista.length === 0 && !abierto && (
        <p className="text-xs text-gray-400 text-center py-2">Sin zonas de trabajo</p>
      )}
    </div>
  );
}

function EditorImportes({ lista, onChange, tiposIva, ivaDefecto }) {
  const [form, setForm] = useState({ importe: '', iva: 21, total: '' });

  useEffect(() => {
    if (ivaDefecto != null) setForm(f => ({ ...f, iva: ivaDefecto }));
  }, [ivaDefecto]);
  const [abierto, setAbierto] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const nuevo = { ...f, [name]: value };
      const imp = parseFloat(nuevo.importe) || 0;
      const iva = parseFloat(nuevo.iva) || 0;
      nuevo.total = (imp + imp * iva / 100).toFixed(2);
      return nuevo;
    });
  };

  const handleAdd = () => {
    if (!form.importe) return;
    onChange([...lista, {
      importe: parseFloat(form.importe),
      iva:     parseFloat(form.iva),
      total:   parseFloat(form.total),
    }]);
    setForm({ importe: '', iva: ivaDefecto ?? 21, total: '' });
    setAbierto(false);
  };

  const totalGeneral = lista.reduce((s, i) => s + (Number(i.total) || 0), 0);

  const fmt = v => Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Importes</h3>
          {totalGeneral > 0 && (
            <p className="text-xs text-blue-600 font-medium mt-0.5">{fmt(totalGeneral)} total</p>
          )}
        </div>
        {!abierto && (
          <button onClick={() => setAbierto(true)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {lista.map((imp, i) => (
        <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
          <div className="flex gap-4 text-sm">
            <span className="text-gray-700 font-medium">{fmt(imp.importe)}</span>
            <span className="text-gray-400">IVA {imp.iva}%</span>
            <span className="text-gray-900 font-semibold">{fmt(imp.total)}</span>
          </div>
          <button onClick={() => onChange(lista.filter((_, j) => j !== i))}
            className="text-gray-300 hover:text-red-500 ml-2">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      ))}

      {abierto && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Importe €</label>
              <input type="number" name="importe" value={form.importe} onChange={handleChange}
                step="0.01" placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">IVA %</label>
              <select name="iva" value={form.iva} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                {tiposIva.map(t => <option key={t.id} value={t.porcentaje}>{t.porcentaje}%</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Total €</label>
              <input type="number" value={form.total} readOnly
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-gray-100 text-gray-600"/>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition">
              Añadir
            </button>
            <button onClick={() => { setAbierto(false); setForm({ importe: '', iva: ivaDefecto, total: '' }); }}
              className="flex-1 border border-gray-300 text-gray-600 text-xs py-1.5 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {lista.length === 0 && !abierto && (
        <p className="text-xs text-gray-400 text-center py-2">Sin importes</p>
      )}
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function SiniestroDetalle() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const esNuevo  = id === 'nuevo';

  const [s, setS]                   = useState(null);
  const [editando, setEditando]     = useState(esNuevo);
  const [form, setForm]             = useState({});
  const [guardando, setGuardando]   = useState(false);
  const [extensiones, setExtensiones] = useState([]);
  const [error, setError]           = useState(null);
  const [accionando, setAccionando] = useState(null);
  const [errores, setErrores]       = useState({});
  const { confirm, pedirConfirm }   = useConfirm();
  const { tiposIva, ivaDefecto }    = useTiposIva();

  const validar = () => {
    const e = {};
    const max = (campo, n) => { if ((form[campo] || '').length > n) e[campo] = `Máximo ${n} caracteres`; };
    max('nSiniestro', 20);
    max('titular',    40);
    max('domicilio',  40);
    max('localidad',  40);
    max('codPoliza',  20);
    max('telefono1',  15);
    max('telefono2',  15);
    return e;
  };

  // Estado local solo para el formulario de nuevo
  const [nuevosPerjudicados, setNuevosPerjudicados] = useState([]);
  const [nuevosPartes,       setNuevosPartes]       = useState([]);
  const [nuevosImportes,     setNuevosImportes]     = useState([]);
  const [parsandoPdf,        setParsandoPdf]        = useState(false);
  const [creandoExtension,   setCreandoExtension]   = useState(false);
  const inputPdfRef = useRef(null);

  useEffect(() => {
    if (esNuevo) {
      setForm({ emitido: false, facturado: false, anulado: false, cobrado: false, extension: 1, localidad: 'SEVILLA' });
      return;
    }
    setEditando(false);
    siniestrosService.obtener(id)
      .then(r => { setS(r.data); setForm(r.data); setFechaFacturadoInput(r.data.fechaFacturado || ''); })
      .catch(() => setError('No se pudo cargar el siniestro'));
    siniestrosService.extensiones(id)
      .then(r => setExtensiones(r.data))
      .catch(() => {});
  }, [id, esNuevo]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setErrores(prev => ({ ...prev, [name]: undefined }));
    if (name === 'codPoliza') setAvisoPoliza(null);
  };

  const excluirId = esNuevo ? undefined : parseInt(id);

  const handleBlurNSiniestro = async () => {
    const valor = form.nSiniestro?.trim();
    if (!valor) return;
    if (form.extension && form.extension > 1) return;
    if (s && valor === s.nSiniestro?.trim()) return;
    try {
      const { data: existe } = await siniestrosService.existeNSiniestro(valor, excluirId);
      if (existe) {
        setErrores(prev => ({ ...prev, nSiniestro: 'Este número de siniestro ya existe. Busca el siniestro y crea una extensión si quieres añadir otra incidencia.' }));
      }
    } catch { /* ignorar error de red */ }
  };

  const handleBlurCodPoliza = async () => {
    const valor = form.codPoliza?.trim();
    if (!valor) { setAvisoPoliza(null); return; }
    if (form.extension && form.extension > 1) return;
    if (s && valor === s.codPoliza?.trim()) return;
    try {
      const { data: existe } = await siniestrosService.existeCodPoliza(valor, excluirId);
      setAvisoPoliza(existe ? `La póliza "${valor}" ya está asociada a otro siniestro. Puedes guardar igualmente si es un siniestro nuevo.` : null);
    } catch { setAvisoPoliza(null); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsandoPdf(true);
    setError(null);
    try {
      const datos = await parsearAvisoPdf(file);
      setForm(f => ({ ...f, ...datos }));
    } catch (err) {
      console.error('[parsearAvisoPdf] Error:', err);
      setError('No se pudo leer el PDF. Comprueba que es un Aviso a Proveedores de OCASO.');
    } finally {
      setParsandoPdf(false);
      e.target.value = '';
    }
  };

  const guardar = async () => {
    const errs = validar();

    const esExtension = form.extension && form.extension > 1;
    const nSiniestroSinCambios = s && form.nSiniestro?.trim() === s.nSiniestro?.trim();
    if (form.nSiniestro?.trim() && !errs.nSiniestro && !esExtension && !nSiniestroSinCambios) {
      try {
        const { data: existe } = await siniestrosService.existeNSiniestro(form.nSiniestro.trim(), excluirId);
        if (existe) {
          errs.nSiniestro = 'Este número de siniestro ya existe. Busca el siniestro y crea una extensión si quieres añadir otra incidencia.';
        }
      } catch { /* ignorar error de red */ }
    }

    if (Object.keys(errs).length > 0) {
      setErrores(errs);
      if (errs.nSiniestro) {
        setTimeout(() => nSiniestroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      }
      return;
    }

    // Check póliza: si ya existe y el aviso no se ha mostrado aún, mostrarlo y detener
    // Las extensiones comparten póliza con el original — no validar
    // Si la póliza no cambió respecto al original cargado, no validar (evita falso positivo cuando el siniestro tiene extensiones)
    const codPolizaSinCambios = s && form.codPoliza?.trim() === s.codPoliza?.trim();
    if (!avisoPoliza && form.codPoliza?.trim() && !esExtension && !codPolizaSinCambios) {
      try {
        const { data: polizaExiste } = await siniestrosService.existeCodPoliza(form.codPoliza.trim(), excluirId);
        if (polizaExiste) {
          setAvisoPoliza(`La póliza "${form.codPoliza.trim()}" ya está asociada a otro siniestro. Si es correcto, pulsa Guardar de nuevo para continuar.`);
          return;
        }
      } catch { /* ignorar */ }
    }

    if (esNuevo || creandoExtension) {
      const resumen = [
        `${nuevosPerjudicados.length} perjudicado(s)`,
        `${nuevosPartes.length} zona(s) de trabajo`,
        `${nuevosImportes.length} importe(s)`,
      ].join(', ');
      const titulo = creandoExtension ? 'Crear extensión' : 'Crear siniestro';
      const ok = await pedirConfirm({
        titulo,
        mensaje: `¿Crear el siniestro con ${resumen}?`,
      });
      if (!ok) return;
    } else {
      const ok = await pedirConfirm({
        titulo: 'Guardar cambios',
        mensaje: '¿Guardar los cambios del siniestro?',
      });
      if (!ok) return;
    }

    setGuardando(true);
    setError(null);
    try {
      if (esNuevo || creandoExtension) {
        const r = await siniestrosService.crear({ ...form, emitido: true });
        const newId = r.data.id;
        await Promise.all([
          ...nuevosPerjudicados.map(p => perjudicadosService.crear(newId, p)),
          ...nuevosPartes.map(p => partesService.crear(newId, p)),
          ...nuevosImportes.map(i => importesService.crear(newId, i)),
        ]);
        navigate(`/siniestros/${newId}`, { replace: true });
        if (creandoExtension) {
          setCreandoExtension(false);
        }
      } else {
        const r = await siniestrosService.actualizar(id, { ...form, emitido: true });
        setS(r.data);
        setForm(r.data);
        setFechaFacturadoInput(r.data.fechaFacturado || '');
        setEditando(false);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleVerParte = async () => {
    if (!s?.emitido) {
      setAccionando('emitir');
      try {
        const r = await siniestrosService.emitir(id);
        setS(r.data);
        setForm(r.data);
      } catch {
        setError('Error al emitir el parte');
        setAccionando(null);
        return;
      }
      setAccionando(null);
    }
    navigate(`/siniestros/${id}/parte-pdf`);
  };

  const handleConfirmar = async () => {
    const ok = await pedirConfirm({
      titulo: 'Confirmar siniestro',
      mensaje: '¿Confirmar el siniestro? Se asignará número de albarán y quedará marcado como facturado.',
    });
    if (!ok) return;
    setAccionando('confirmar');
    try {
      const r = await siniestrosService.confirmar(id);
      setS(r.data);
      setForm(r.data);
      setFechaFacturadoInput(r.data.fechaFacturado || '');
    } catch {
      setError('Error al confirmar');
    } finally {
      setAccionando(null);
    }
  };

  const handleGuardarFechaFacturado = async () => {
    setGuardandoFecha(true);
    try {
      const r = await siniestrosService.actualizar(id, { ...s, fechaFacturado: fechaFacturadoInput || null });
      setS(r.data);
      setFechaFacturadoInput(r.data.fechaFacturado || '');
    } catch {
      setError('Error al guardar la fecha de albarán');
    } finally {
      setGuardandoFecha(false);
    }
  };

  const handleCobrar = async () => {
    const ok = await pedirConfirm({
      titulo: 'Marcar como cobrado',
      mensaje: '¿Marcar este siniestro como cobrado?',
      textOk: 'Cobrado',
    });
    if (!ok) return;
    setAccionando('cobrar');
    try {
      const hoy = new Date().toISOString().split('T')[0];
      const actualizado = { ...s, cobrado: true, fechaFacturado: s.fechaFacturado || hoy };
      const r = await siniestrosService.actualizar(id, actualizado);
      setS(r.data);
      setForm(r.data);
    } catch {
      setError('Error al marcar como cobrado');
    } finally {
      setAccionando(null);
    }
  };

  const abrirModalAnular = () => {
    setFechaAnulacion(new Date().toISOString().split('T')[0]);
    setModalAnular(true);
  };

  const handleAnular = async () => {
    setAccionando('anular');
    setModalAnular(false);
    try {
      await siniestrosService.anular(id, fechaAnulacion);
      navigate('/siniestros');
    } catch {
      setError('Error al anular el siniestro');
      setAccionando(null);
    }
  };

  const [avisoPoliza, setAvisoPoliza]             = useState(null);
  const nSiniestroRef = useRef(null);
  const [fechaFacturadoInput, setFechaFacturadoInput] = useState('');
  const [guardandoFecha, setGuardandoFecha]           = useState(false);
  const [modalAnular, setModalAnular]                 = useState(false);
  const [fechaAnulacion, setFechaAnulacion]           = useState('');

  const abrirCrearExtension = () => {
    const todasLasExtensiones = [s, ...extensiones].map(e => e.extension || 1);
    const siguienteExtension  = Math.max(...todasLasExtensiones) + 1;
    setCreandoExtension(true);
    setEditando(true);
    setNuevosPerjudicados([]);
    setNuevosPartes([]);
    setNuevosImportes([]);
    setForm({
      nSiniestro:      s.nSiniestro,
      codPoliza:       s.codPoliza,
      titular:         s.titular,
      domicilio:       s.domicilio,
      localidad:       s.localidad,
      telefono1:       s.telefono1,
      telefono2:       s.telefono2,
      perito:          s.perito,
      peritoMovil:     s.peritoMovil,
      fechaAsignacion: s.fechaAsignacion,
      extension:       siguienteExtension,
      emitido:         false,
      facturado:       false,
      anulado:         false,
      cobrado:         false,
      notas:           '',
      albaran:         null,
      fechaFacturado:  null,
    });
  };

  if (error && !esNuevo) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-red-600">{error}</p>
    </div>
  );

  if (!esNuevo && !s) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"/>
    </div>
  );

  const titulo = esNuevo ? 'Nuevo siniestro' : (s?.titular || 'Siniestro');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">

        {/* ── Cabecera ── */}
        <div className="space-y-2">
          {/* Fila 1: navegación + título + Editar/Cancelar */}
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/siniestros')}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold shrink-0">←</button>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-lg text-gray-800 truncate">{titulo}</h2>
              {s?.nSiniestro && (
                <p className="text-gray-400 text-sm">
                  {s.nSiniestro} · Ext. {creandoExtension ? form.extension : s.extension}
                  {s.codPoliza && <span className="ml-2 text-gray-500 font-medium">Póliza: {s.codPoliza}</span>}
                </p>
              )}
            </div>
            {editando && !esNuevo && (
              <button onClick={() => setEditando(false)}
                className="shrink-0 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
                Cancelar
              </button>
            )}
            {!editando && !esNuevo && (
              <button onClick={() => { setForm({ ...s }); setEditando(true); }}
                className="shrink-0 bg-primary-600 text-white font-semibold px-3 py-1.5 rounded-lg text-sm hover:bg-primary-700 transition">
                Editar
              </button>
            )}
          </div>

          {/* Fila 2: botones de acción (solo en vista, no edición) */}
          {!editando && !esNuevo && (
            <div className="flex flex-wrap gap-1.5">
              {/* Ver parte */}
              <button onClick={handleVerParte} disabled={!!accionando}
                title="Ver parte de trabajo"
                className="flex items-center gap-1.5 bg-gray-600 hover:bg-gray-700 text-white
                           text-sm font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                </svg>
                <span className="hidden sm:inline">Ver parte</span>
              </button>

              {/* Confirmar — solo si emitido y no facturado */}
              {s?.emitido && !s?.facturado && (
                <button onClick={handleConfirmar} disabled={!!accionando}
                  title="Asigna albarán y marca como facturado"
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white
                             text-sm font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50">
                  {accionando === 'confirmar' ? '...' : (
                    <>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span className="hidden sm:inline">Confirmar</span>
                    </>
                  )}
                </button>
              )}

              {/* Cobrado — solo si facturado y no cobrado */}
              {s?.facturado && !s?.cobrado && (
                <button onClick={handleCobrar} disabled={!!accionando}
                  title="Marcar como cobrado"
                  className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white
                             text-sm font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50">
                  {accionando === 'cobrar' ? '...' : (
                    <>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span className="hidden sm:inline">Cobrado</span>
                    </>
                  )}
                </button>
              )}

              {/* Crear extensión — solo desde siniestro base (extension = 1) */}
              {(!s?.extension || s.extension <= 1) && !creandoExtension && (
              <button onClick={abrirCrearExtension} disabled={!!accionando}
                title="Crear extensión del siniestro"
                className="flex items-center gap-1.5 border border-primary-400 text-primary-700
                           hover:bg-primary-50 text-sm font-semibold px-3 py-1.5 rounded-lg
                           transition disabled:opacity-50">
                {creandoExtension ? '...' : (
                  <>
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                    <span className="hidden sm:inline">Extensión</span>
                  </>
                )}
              </button>
              )}

              {/* Anular — solo si no está ya anulado */}
              {!s?.anulado && (
                <button onClick={abrirModalAnular} disabled={!!accionando}
                  title="Anular siniestro"
                  className="flex items-center gap-1.5 border border-red-300 text-red-600
                             hover:bg-red-50 text-sm font-semibold px-3 py-1.5 rounded-lg
                             transition disabled:opacity-50">
                  {accionando === 'anular' ? '...' : (
                    <>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                      </svg>
                      <span className="hidden sm:inline">Anular</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Botón subir PDF (solo nuevo) ── */}
        {esNuevo && (
          <div
            onClick={() => inputPdfRef.current?.click()}
            className="cursor-pointer bg-amber-50 border-2 border-dashed border-amber-400
                       rounded-xl px-4 py-4 flex items-center gap-3 hover:bg-amber-100 transition"
          >
            <div className="bg-amber-400 rounded-lg p-2 shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">
                {parsandoPdf ? 'Leyendo PDF...' : 'Cargar Aviso a Proveedores (PDF)'}
              </p>
              <p className="text-xs text-amber-600">Precarga automática de datos desde el PDF de OCASO</p>
            </div>
            {parsandoPdf && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600 shrink-0"/>
            )}
          </div>
        )}
        <input ref={inputPdfRef} type="file" accept=".pdf" onChange={handlePdfUpload} className="hidden"/>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* ── Estados (solo vista) ── */}
        {!editando && s && (
          <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-4">
            <EstadoToggle label="Emitido"   activo={s.emitido}   color="blue" />
            <EstadoToggle label="Facturado" activo={s.facturado} color="yellow" />
            <EstadoToggle label="Cobrado"   activo={s.cobrado}   color="green" />
            <EstadoToggle label="Anulado"   activo={s.anulado}   color="red" />
          </div>
        )}

        {editando ? (
          /* ── Formulario ── */
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                {esNuevo ? 'Datos del siniestro' : 'Editando'}
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['emitido', 'facturado', 'cobrado', 'anulado'].map(campo => (
                  <label key={campo} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name={campo} checked={!!form[campo]} onChange={handleChange} className="rounded"/>
                    <span className="text-sm capitalize">{campo}</span>
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                {[
                  { name: 'nSiniestro',      label: 'Nº Siniestro',    type: 'text',   max: 20 },
                  { name: 'codPoliza',       label: 'Póliza',           type: 'text',   max: 20 },
                  { name: 'titular',         label: 'Titular',          type: 'text',   max: 40 },
                  { name: 'domicilio',       label: 'Domicilio',        type: 'text',   max: 40 },
                  { name: 'localidad',       label: 'Localidad',        type: 'text',   max: 40 },
                  { name: 'telefono1',       label: 'Teléfono 1',       type: 'tel',    max: 15 },
                  { name: 'telefono2',       label: 'Teléfono 2',       type: 'tel',    max: 15 },
                  { name: 'fechaAsignacion', label: 'Fecha asignación', type: 'date' },
                  { name: 'fechaFacturado',  label: 'Fecha albarán',    type: 'date' },
                  { name: 'albaran',         label: 'Albarán',          type: 'number' },
                ].map(({ name, label, type, max }) => {
                  const esExtension = form.extension && form.extension > 1;
                  const estaBloqueado = esExtension && (name === 'nSiniestro' || name === 'codPoliza');
                  return (
                  <div key={name} className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1">{label}</label>
                    <input
                      disabled={estaBloqueado}
                      ref={name === 'nSiniestro' ? nSiniestroRef : undefined}
                      type={type} name={name} value={form[name] || ''} onChange={handleChange}
                      onBlur={name === 'nSiniestro' ? handleBlurNSiniestro :
                              name === 'codPoliza'   ? handleBlurCodPoliza   : undefined}
                      maxLength={max}
                      className={`w-full min-w-0 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2
                        ${estaBloqueado ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' :
                          errores[name]
                          ? 'border-red-400 focus:ring-red-300 bg-red-50'
                          : 'border-gray-300 focus:ring-primary-500'}`}/>
                    {errores[name] && (
                      <p className="text-xs text-red-600 mt-0.5 font-medium">{errores[name]}</p>
                    )}
                    {max && !errores[name] && (form[name] || '').length > max * 0.8 && (
                      <p className="text-xs text-gray-400 mt-0.5 text-right">
                        {(form[name] || '').length}/{max}
                      </p>
                    )}
                  </div>
                  );
                })}

                {/* Selector de perito — ocupa las 2 columnas */}
                <PeritoSelector
                  value={{ perito: form.perito || '', peritoMovil: form.peritoMovil || '' }}
                  onChange={({ perito, peritoMovil }) =>
                    setForm(f => ({ ...f, perito, peritoMovil }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Notas</label>
                <textarea name="notas" value={form.notas || ''} onChange={handleChange} rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
              </div>
            </div>

            {/* Secciones de datos relacionados — siempre visibles */}
            {esNuevo || creandoExtension ? (
              <>
                <EditorPerjudicados lista={nuevosPerjudicados} onChange={setNuevosPerjudicados} />
                <EditorPartes       lista={nuevosPartes}       onChange={setNuevosPartes} />
                <EditorImportes     lista={nuevosImportes}     onChange={setNuevosImportes} tiposIva={tiposIva} ivaDefecto={ivaDefecto} />
              </>
            ) : (
              <>
                <PerjudicadosPanel idSiniestro={parseInt(id)} soloLectura={!editando} />
                <ParteTrabajoPanel idSiniestro={parseInt(id)} soloLectura={!editando} />
                <ImportesPanel     idSiniestro={parseInt(id)} soloLectura={!editando} />
              </>
            )}

            {/* Aviso póliza duplicada */}
            {avisoPoliza && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3">
                <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                </svg>
                <p className="text-sm text-amber-800 flex-1">{avisoPoliza}</p>
                <button onClick={() => setAvisoPoliza(null)} className="text-amber-500 hover:text-amber-700 shrink-0 text-lg leading-none">×</button>
              </div>
            )}

            {/* Botones guardar */}
            <div className="flex gap-3 pb-4">
              <button onClick={guardar} disabled={guardando}
                className="flex-1 bg-primary-600 text-white font-semibold py-2.5 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition">
                {guardando ? 'Guardando...' : '💾 Guardar'}
              </button>
              {(!esNuevo || creandoExtension) && (
                <button onClick={() => {
                  if (creandoExtension) {
                    setCreandoExtension(false);
                    setEditando(false);
                    setNuevosPerjudicados([]);
                    setNuevosPartes([]);
                    setNuevosImportes([]);
                    setForm(s);
                  } else {
                    setEditando(false);
                  }
                }}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Cancelar
                </button>
              )}
            </div>
          </div>

        ) : (
          /* ── Vista detalle ── */
          <>
            {/* Identificación */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Identificación</h3>
              {/* Siniestro · Extensión · Póliza en la misma línea */}
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Nº Siniestro" valor={s.nSiniestro || '—'} />
                <Campo label="Extensión"    valor={String(s.extension ?? '—')} />
                <Campo label="Póliza"       valor={s.codPoliza} />
              </div>
              {/* Fecha asignación sola */}
              <Campo label="Fecha asignación" valor={s.fechaAsignacion && new Date(s.fechaAsignacion).toLocaleDateString('es-ES')} />
              {/* Albarán + Fecha albarán — fecha siempre editable */}
              <div className="grid grid-cols-2 gap-3 items-end">
                <Campo label="Albarán" valor={s.albaran != null ? String(Math.round(s.albaran)) : null} />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha albarán</p>
                  <div className="flex gap-1.5 items-center">
                    <input
                      type="date"
                      value={fechaFacturadoInput}
                      onChange={e => setFechaFacturadoInput(e.target.value)}
                      className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {fechaFacturadoInput !== (s.fechaFacturado || '') && (
                      <button
                        onClick={handleGuardarFechaFacturado}
                        disabled={guardandoFecha}
                        className="shrink-0 bg-primary-600 text-white text-xs font-semibold
                                   px-2 py-1 rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                      >
                        {guardandoFecha ? '...' : 'OK'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Titular */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Titular</h3>
              <Campo label="Nombre"    valor={s.titular} />
              <Campo label="Domicilio" valor={s.domicilio} />
              <Campo label="Localidad" valor={s.localidad} />
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Teléfono"            valor={s.telefono1} href={s.telefono1 ? `tel:${s.telefono1}` : null} />
                <Campo label="Teléfono alternativo" valor={s.telefono2} href={s.telefono2 ? `tel:${s.telefono2}` : null} />
              </div>
            </div>

            {/* Perito */}
            {(s.perito || s.peritoMovil) && (
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Perito</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Nombre" valor={s.perito} />
                  <Campo label="Móvil"  valor={s.peritoMovil} href={s.peritoMovil ? `tel:${s.peritoMovil}` : null} />
                </div>
              </div>
            )}

            {/* Observaciones */}
            {s.notas && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Observaciones</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{s.notas}</p>
              </div>
            )}

            {extensiones.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Extensiones</p>
                <div className="space-y-2">
                  {extensiones.map(e => (
                    <div key={e.id} onClick={() => navigate(`/siniestros/${e.id}`)}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <span className="text-sm">Extensión {e.extension}</span>
                      <span className="text-xs text-gray-400">
                        {e.fechaAsignacion && new Date(e.fechaAsignacion).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <PerjudicadosPanel idSiniestro={parseInt(id)} soloLectura />
            <ParteTrabajoPanel idSiniestro={parseInt(id)} soloLectura />
            <ImportesPanel     idSiniestro={parseInt(id)} soloLectura />

            <div className="pb-8" />
          </>
        )}

      </div>
      <ConfirmDialog {...confirm} abierto={confirm !== null} />

      {/* ── Modal anulación ── */}
      {modalAnular && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 rounded-full p-2 shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 text-lg">Anular siniestro</h3>
            </div>
            <p className="text-sm text-gray-600">
              Esta acción marcará el siniestro <strong>{s?.nSiniestro}</strong> como anulado.
              Esta operación no se puede deshacer fácilmente.
            </p>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                Fecha de anulación
              </label>
              <input
                type="date"
                value={fechaAnulacion}
                onChange={e => setFechaAnulacion(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-400"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={handleAnular}
                disabled={!fechaAnulacion}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5
                           rounded-xl text-sm transition disabled:opacity-50"
              >
                Anular siniestro
              </button>
              <button
                onClick={() => setModalAnular(false)}
                className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2.5
                           rounded-xl text-sm hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
